import type { TwnBadgeProps } from '@/components/atoms/prime/badge/twn-badge/TwnBadge.types'
import type { TwnTagProps } from '@/components/atoms/prime/tag/twn-tag/TwnTag.types'
import type { IconName } from '@/types/const/IconAlias.consts'

type IndicatorSeverity = NonNullable<TwnTagProps['severity']>

/**
 * Which severities describe a state that has settled, and which describe one that still wants
 * somebody to act.
 *
 * Shopify's admin stays quiet about a settled state: "Paid" and "Fulfilled" sit on a plain grey pill.
 * Colour is reserved for a row the merchant has to do something
 * about — "Ready for pickup" is cyan because it is a job, not a result. A table where every row shouts
 * tells the eye nothing.
 */
const SETTLED_SEVERITIES: readonly string[] = ['success', 'secondary', 'contrast']

/**
 * The rule itself, as a function rather than a branch inside the component, so a test can assert what
 * the badge does rather than what a list contains.
 *
 * Returns the severity the pill wears: `secondary` for a settled state, and the severity itself for a
 * state that wants action.
 */
const pillSeverityFor = (severity: IndicatorSeverity): IndicatorSeverity =>
  SETTLED_SEVERITIES.includes(severity) ? 'secondary' : severity

interface TwnIndicatorProps {
  /** Drives the default label and, with no `severity`, the default colour. */
  state?: boolean
  /** Overrides the label drawn from `state`. */
  label?: string
  /**
   * What the state means. A settled one renders quiet; one that wants action takes the colour.
   * `null` is allowed because several call sites map an unknown status to it.
   */
  severity?: TwnTagProps['severity'] | null
  /** `small` drops the label to 11px. Every other size keeps the badge's own 12px. */
  size?: TwnBadgeProps['size']
  /** Pulses the dot while something is still running. Respects a reduced-motion preference. */
  animate?: boolean
  /** Replaces the dot: the colour says how urgent the state is, the icon says what happened. */
  icon?: IconName
  /** Draws `icon` solid, as Shopify's full circle marks a payment that is done. */
  isIconFilled?: boolean
}

export type { IndicatorSeverity, TwnIndicatorProps }
export { pillSeverityFor, SETTLED_SEVERITIES }
